/*
  Conditional console logger for BA frontend

  Behavior:
  - Hooks into console.* by default. Output is suppressed unless BA_DEBUG is present in localStorage.
  - BA_DEBUG values:
      "1", "true", "*" => enable all console output
      "ns1,ns2"          => enable only those namespaces when using createLogger("ns"). Global console.* remains suppressed.
  - Query param support: ?ba_debug=*, ?ba_debug=ns1,ns2 will persist to localStorage.
  - Window helpers:
      window.baDebugEnable('*' | 'ns1,ns2')
      window.baDebugDisable()

  Usage examples:
    import { createLogger } from '../lib/logger'
    const log = createLogger('cart')
    log.info('Cart mounted')
*/

const LS_KEY = 'BA_DEBUG';

export type CustomLogger = {
  log: (...args: any[]) => void;
  info: (...args: any[]) => void;
  warn: (...args: any[]) => void;
  error: (...args: any[]) => void;
  debug: (...args: any[]) => void;
};

// Preserve originals to avoid recursion and allow true output when enabled
const original = {
  log: console.log.bind(console),
  info: console.info.bind(console),
  warn: console.warn.bind(console),
  error: console.error.bind(console),
  debug: console.debug ? console.debug.bind(console) : console.log.bind(console),
};

type ConsoleMethod = keyof typeof original;

function parseDebugValue(raw: string | null): { all: boolean; namespaces: Set<string> } {
  if (!raw) return { all: false, namespaces: new Set() };
  const val = String(raw).trim();
  if (val === '1' || val.toLowerCase() === 'true' || val === '*') {
    return { all: true, namespaces: new Set() };
  }
  const parts = val
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  return { all: false, namespaces: new Set(parts) };
}

function getState() {
  try {
    return parseDebugValue(window.localStorage.getItem(LS_KEY));
  } catch {
    return { all: false, namespaces: new Set<string>() };
  }
}

function isEnabled(namespace?: string): boolean {
  const state = getState();
  if (state.all) return true;
  if (!namespace) return false;
  return state.namespaces.has(namespace);
}

function setDebugValue(value: string) {
  try {
    window.localStorage.setItem(LS_KEY, value);
  } catch {
    // ignore storage failures (e.g., private mode)
  }
}

function clearDebugValue() {
  try {
    window.localStorage.removeItem(LS_KEY);
  } catch {
    // ignore
  }
}

// Generate a stable color for a namespace
function colorFor(ns: string): string {
  let hash = 5381;
  for (let i = 0; i < ns.length; i++) {
    hash = (hash << 5) + hash + ns.charCodeAt(i);
  }
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue} 70% 45%)`;
}

function nsStyle(ns: string): string {
  return `color: ${colorFor(ns)}; font-weight: 600;`;
}

function conditional(method: ConsoleMethod, namespace?: string) {
  return (...args: any[]) => {
    if (isEnabled(namespace)) {
      if (namespace) {
        original[method](
          `%c[${namespace}]%c`,
          nsStyle(namespace),
          'color: inherit; font-weight: inherit;',
          ...args,
        );
      } else {
        original[method](...args);
      }
    }
  };
}

export function createLogger(namespace: string): CustomLogger {
  return {
    log: conditional('log', namespace),
    info: conditional('info', namespace),
    warn: conditional('warn', namespace),
    error: conditional('error', namespace),
    debug: conditional('debug', namespace),
  };
}

// Support query param to quickly enable
(function readQueryParamOnce() {
  try {
    const url = new URL(window.location.href);
    const qp = url.searchParams.get('ba_debug');
    if (qp) {
      setDebugValue(qp);
    }
  } catch {
    // ignore
  }
})();

// Expose helpers on window for quick toggling during development
declare global {
  interface Window {
    enableLogging?: (value?: string) => void;
    disableLogging?: () => void;
  }
}

window.enableLogging = (value = '*') => setDebugValue(value);
window.disableLogging = () => clearDebugValue();
